import * as React from 'react';
import { useParams } from 'react-router';
import gql from 'graphql-tag';
import pluralize from 'pluralize';

import { colName, DataTable, DataTableHandle, DataTableViewChangeType, DropdownField, Panel, Part, Section, useForceUpdate, useForm, useFormToUrl } from 'app2/components';
import { HrDataTable, useCols, useQuery } from 'app2/views/shared';

import { entityTypes } from '../shared';
import { AdminPage } from '../AdminPage';

import { AdminQueryTableRouteParams } from './AdminQueryTableRouteParams';

export function QueryTable() {
  const { type } = useParams<AdminQueryTableRouteParams>();
  const form = useForm({type}, []);
  useFormToUrl(form, {path: '/admin2/query/:type'});

  const update = useForceUpdate();
  const visibleCols = React.useRef<string[]>([]);
  const cols = getCols();
  const table = React.useRef<DataTableHandle>();
  
  function render() {
    return <AdminPage title='Query'>
      <Panel type='edit-no-save-button' form={form} onNavigation='nothing'>
        <Section>
          <Part label='Type' name='type' component={DropdownField} options={entityTypes} />
        </Section>
      </Panel>

      <HrDataTable query={query()}
        views={{  entityKind: getType() as any }}
        header={{ title: getType(), groupByEnabled: true, groupByOptions: undefined }}
        table={{ rowSelection:false, ref: table, cols, cellStyle: 'read-only', onViewChange}} />
    </AdminPage>
  }

  function getType() {
    return form.values.type || 'Site';
  }

  function getCols() {
    const cols = useCols(getType());

    return React.useMemo(() => {
      visibleCols.current = ['id'];
      return cols.map(c => ({...c, hidden: c.name != 'id'}));
    }, [getType()]);
  }

  function onViewChange(table:DataTable, changeType:DataTableViewChangeType) {
    if (changeType != 'col-visibility') {
      return;
    }

    visibleCols.current = table.cols.map(c => colName(c));
    update();
  }

  function query() {
    return React.useMemo(() => createQuery(), [getType(), visibleCols.current]);
  }

  function createQuery() {
    const name = pluralize(getType()).toLowerCase();
    const query = gql`
      query AdminQueryTable {
        ${name}Query(pageSize: 10) {
          items {
            ${createQuerySelection()}
          }
        }
      }    
    `

    return () => useQuery({ query });
  }

  function createQuerySelection():string {
    const selections:any = {};
    visibleCols.current.forEach(path => {
      let cur = selections;
      const parts = path.split('.');

      parts.forEach(part => {
        cur = cur[part] ||= {};
      });
    })

    return addSelections(selections);
  }

  function addSelections(selections:any) {
    if (!selections) {
      return '';
    }

    let s = '';

    Object.keys(selections).forEach(part => {
      s += ' ' + part;

      const children = addSelections(selections[part]);

      if (children.length) {
        s += `{${children}}`;
      }
    });

    return s;
  }

  return render();
}
